import apiClient from '@/services/axios'

/**
 * Drive API
 *
 * Endpoints (backend to implement):
 *   GET    /drive           → { files: DriveFile[], storage: { used: number, total: number } }
 *   POST   /drive/presign   → { url: string, key: string, fileId: string }
 *   POST   /drive/commit    → DriveFile
 *   DELETE /drive/:fileId
 *   PATCH  /drive/:fileId   → DriveFile
 *
 * DriveFile shape:
 *   { id, name, ext, size, status, starred, tags, createdAt, updatedAt, deletedAt, url }
 */

export function listDriveFiles() {
  return apiClient.get('/drive')
}

export function presignDriveUpload(filename, contentType, size) {
  return apiClient.post('/drive/presign', { filename, contentType, size })
}

export function commitDriveUpload(fileId, key) {
  return apiClient.post('/drive/commit', { fileId, key })
}

export function deleteDriveFile(fileId) {
  return apiClient.delete(`/drive/${fileId}`)
}

export function updateDriveFile(fileId, data) {
  return apiClient.patch(`/drive/${fileId}`, data)
}

export function getDriveFileSignedUrl(fileId, disposition) {
  const params = disposition ? { disposition } : {}
  return apiClient.get(`/drive/${fileId}/signed-url`, { params })
}

/**
 * Full upload flow: presign → PUT to S3 → commit
 * @param {File} file
 * @param {function} onProgress - called with 0-100 number
 * @returns {Promise<DriveFile>}
 */
export async function uploadDriveFile(file, onProgress) {
  const { data: presignRes } = await presignDriveUpload(file.name, file.type, file.size)
  const { url, key, fileId } = presignRes

  onProgress && onProgress(10)

  const putRes = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  })

  if (!putRes.ok) throw new Error('S3 upload failed')

  onProgress && onProgress(80)

  const { data: commitRes } = await commitDriveUpload(fileId, key)

  onProgress && onProgress(100)

  return commitRes
}
